<template>
    <div class="confirmation-toast">
        <div class="toast-content">
            <div class="toast-header">
                <div class="icon-wrapper">
                    <Icon :name="icon" />
                </div>
                <button class="close-button" type="button" @click="handleClose">
                    <Icon name="interface_close" :color="'dark'" :size="'small'" />
                </button>
            </div>
            <div class="toast-body">
                <h3 class="toast-title">{{ title }}</h3>
                <p v-if="description" class="toast-description">{{ description }}</p>
            </div>
            <div v-if="showAction" class="toast-actions">
                <button class="toast-action-button" type="button" @click="handleAction">
                    {{ actionLabel }}
                </button>
            </div>
            <div v-if="timeToClose > 0" class="progress-container">
                <div class="progress-bar" :style="{ width: `${currentProgress}%` }"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onUnmounted } from 'vue';
import Icon from '@components/icons/Icon.vue';

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    description: {
        type: String,
        default: ''
    },
    icon: {
        type: String,
        default: 'confirmation_toast_icon'
    },
    timeToClose: {
        type: Number,
        default: 5000
    },
    showAction: {
        type: Boolean,
        default: false
    },
    actionLabel: {
        type: String,
        default: 'Confirm'
    }
});

const emit = defineEmits(['close', 'action']);

const currentProgress = ref(100);
let progressInterval = null;
let closeTimeout = null;
let isRunning = false;

const handleClose = () => {
    isRunning = false;
    if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
    }
    if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
    }
    emit('close');
};

const handleAction = () => {
    emit('action');
};

onMounted(() => {
    // Only start auto-close timer if timeToClose is a valid number
    if (props.timeToClose && props.timeToClose > 0) {
        const startTime = Date.now();
        isRunning = true;
        
        progressInterval = setInterval(() => {
            if (!isRunning) return;
            
            const elapsed = Date.now() - startTime;
            const progress = Math.max(100 - (elapsed / props.timeToClose) * 100, 0);
            currentProgress.value = progress;
            
            if (progress <= 0) {
                clearInterval(progressInterval);
                progressInterval = null;
            }
        }, 50);
        
        closeTimeout = setTimeout(() => {
            handleClose();
        }, props.timeToClose);
    }
});

onUnmounted(() => {
    if (progressInterval) {
        clearInterval(progressInterval);
    }
    if (closeTimeout) {
        clearTimeout(closeTimeout);
    }
});

</script>

<style scoped>
.confirmation-toast {
    width: 100%;
    max-width: 504px;
}

.toast-content {
    background-color: #ffffff;
    border: 1px solid #d0d5dd;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.03),
                0px 12px 16px -4px rgba(16, 24, 40, 0.08);
    position: relative;
}

.toast-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
}

.icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    left: -8px;
}

.close-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.25s;
    color: #b7b9bc;
}

.close-button:hover {
    background-color: #ebeced;
}

.toast-body {
    margin-bottom: 12px;
}

.toast-title {
    color: #101828;
    font-size: 14px;
    font-family: Inter, sans-serif;
    font-weight: 600;
    line-height: 20px;
    margin: 0 0 8px 0;
    word-wrap: break-word;
}

.toast-description {
    color: #475467;
    font-size: 14px;
    font-family: Inter, sans-serif;
    font-weight: 400;
    line-height: 20px;
    margin: 0;
    text-align: left;
}

.toast-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
}

.toast-action-button {
    background-color: #7F56D9;
    border: none;
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    padding: 8px 14px;
    transition: background-color 0.2s ease;
}

.toast-action-button:hover {
    background-color: #6941C6;
}

.progress-container {
    height: 8px;
    width: 100%;
    background-color: #EAECF0;
    border-radius: 100px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    background-color: #7F56D9;
    border-radius: 100px;
    transition: width 0.3s ease;
}

</style>